import { FC, useRef } from 'react';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { SquarePen, UserRoundPlus, UserPlus, EyeOff, Ellipsis } from 'lucide-react';

export type ClassMenuAction = 'edit' | 'addStudents' | 'addCoTeacher' | 'hide';

interface Props {
  name: string;
  /** Clever-synced classes can only be hidden; every other action is disabled. */
  isClever?: boolean;
  onAction: (action: ClassMenuAction) => void;
}

const ClassesRosterCardMenu: FC<Props> = ({ name, isClever = false, onAction }) => {
  const menu = useRef<Menu>(null);

  // PrimeReact's Menu owns the ARIA menu semantics + keyboard navigation (arrows,
  // Home/End, Enter, Escape); we just supply the model.
  const items: MenuItem[] = [
    {
      label: 'Edit class',
      icon: <SquarePen size={16} aria-hidden />,
      disabled: isClever,
      command: () => onAction('edit'),
    },
    {
      label: 'Manage students',
      icon: <UserPlus size={16} aria-hidden />,
      disabled: isClever,
      command: () => onAction('addStudents'),
    },
    {
      label: 'Manage co-teachers',
      icon: <UserRoundPlus size={16} aria-hidden />,
      disabled: isClever,
      command: () => onAction('addCoTeacher'),
    },
    {
      label: 'Hide class',
      icon: <EyeOff size={16} aria-hidden />,
      className: 'nc_class-menu__danger',
      command: () => onAction('hide'),
    },
    ...(isClever
      ? [
          {
            template: () => <p className="nc_class-menu__note">Class editing and rostering are managed in Clever.</p>,
          } as MenuItem,
        ]
      : []),
  ];

  return (
    <>
      <button
        type="button"
        aria-label={`Actions for ${name}`}
        aria-haspopup
        className="nc_class-card__menu"
        onClick={event => {
          event.stopPropagation();
          menu.current?.toggle(event);
        }}
      >
        <Ellipsis size={20} aria-hidden />
      </button>

      <Menu model={items} popup ref={menu} className="nc_class-menu" appendTo={document.body} />
    </>
  );
};

export default ClassesRosterCardMenu;
